// The fixed-position left rail. Composes the per-archetype cards.

import { Badge } from '@aod/ui/components/ui/badge';
import { ScrollArea } from '@aod/ui/components/ui/scroll-area';
import { Separator } from '@aod/ui/components/ui/separator';
import { ScrollText } from 'lucide-react';
import { useState } from 'react';
import { useT } from '../../i18n/index.js';
import { plural, rich } from '../../i18n/rich.js';
import { appDom } from '../../lib/appDom.js';
import {
  PanelCollapseButton,
  PanelDockGrip,
  PanelRailButton,
  PanelResizeHandle,
  useIsDesktop,
  usePanelDockStyle,
} from '../layout/PanelDockControls.js';
import { usePanelLayout } from '../layout/usePanelLayout.js';
import { type Artifact, ArtifactsPanel } from './ArtifactsPanel.js';
import { DoctrineCard } from './DoctrineCard.js';
import { ARCHETYPES, type Archetype, TIER_CAP } from './archetypes.js';
import { doctrineGateReason } from './gating.js';
import type { DoctrineState } from './types.js';

interface DoctrinePanelProps {
  readonly state: DoctrineState | null;
  readonly currentTick: bigint;
  readonly onInvest: (archetype: Archetype) => void;
  readonly artifacts?: Artifact[];
  readonly onInstallArtifact?: (artifactId: string) => void;
  /** Below md the rail is a drawer: hidden unless opened from the HUD toggles. */
  readonly mobileOpen?: boolean;
}

export function DoctrinePanel({
  state,
  currentTick,
  onInvest,
  artifacts = [],
  onInstallArtifact,
  mobileOpen = false,
}: DoctrinePanelProps) {
  const t = useT();
  const [activeTab, setActiveTab] = useState<'doctrine' | 'artifacts'>('doctrine');
  const ticksUntilNextGrant =
    state === null ? null : Math.max(0, state.nextGrantTick - Number(currentTick));
  // Panel-dock store: size/shift ride on the root's inline style; a collapsed
  // panel becomes a rail icon (desktop only — the phone drawer keeps today's
  // behaviour and ignores the store below md).
  const dockStyle = usePanelDockStyle('doctrine', true);
  const collapsed = usePanelLayout((s) => s.panels.doctrine.collapsed);
  const isDesktop = useIsDesktop();

  if (collapsed && isDesktop) {
    return (
      <PanelRailButton
        id="doctrine"
        label={t('doctrine.label')}
        icon={<ScrollText className="size-4" />}
      />
    );
  }

  return (
    <section
      aria-label={t('doctrine.label')}
      data-testid="doctrine-panel"
      {...appDom({
        id: 'doctrine-panel',
        type: 'Sidebar',
        scope: 'aside',
        label: t('doctrine.label'),
      })}
      style={dockStyle}
      className={`fixed top-(--hud-panel-top) bottom-16 left-3 z-40 md:bottom-4 ${mobileOpen ? 'flex' : 'hidden'} w-(--rail-w) max-w-[calc(100vw-1.5rem)] flex-col gap-2 rounded-xl border border-border/70 bg-card/95 p-3 text-foreground shadow-xl backdrop-blur-sm md:flex`}
    >
      <TabBar activeTab={activeTab} onTabChange={setActiveTab} />
      <Separator />
      {activeTab === 'doctrine' ? (
        <>
          <PanelHeader state={state} ticksUntilNextGrant={ticksUntilNextGrant} />
          <Separator />
          <ScrollArea className="-mx-1 min-h-0 flex-1 px-1">
            <div className="flex flex-col gap-2 py-1">
              {ARCHETYPES.map((arch, i) => {
                const points = state?.spent[i] ?? 0;
                const atCap = points >= TIER_CAP;
                const canInvest = (state?.available ?? 0) > 0 && !atCap;
                const gateReason = doctrineGateReason(state, i);
                return (
                  <DoctrineCard
                    key={arch.id}
                    meta={arch}
                    points={points}
                    canInvest={canInvest}
                    gateReason={gateReason}
                    onInvest={() => onInvest(arch.id)}
                  />
                );
              })}
            </div>
          </ScrollArea>
          <p className="px-1 pt-1 text-[10px] leading-snug text-muted-foreground">
            {rich(t('doctrine.hybridHint'), {
              forms: <strong>{t('doctrine.hybridForms')}</strong>,
            })}
          </p>
        </>
      ) : (
        <ArtifactsPanel artifacts={artifacts} onInstall={onInstallArtifact ?? (() => {})} />
      )}
      <PanelResizeHandle id="doctrine" side="right" />
    </section>
  );
}

interface PanelHeaderProps {
  readonly state: DoctrineState | null;
  readonly ticksUntilNextGrant: number | null;
}

function PanelHeader({ state, ticksUntilNextGrant }: PanelHeaderProps) {
  const t = useT();
  return (
    <header className="flex flex-col gap-1 px-1 pt-1 pb-2">
      <div className="flex items-baseline justify-between">
        <span className="text-[11px] font-semibold tracking-[0.18em] text-muted-foreground">
          {t('doctrine.title')}
        </span>
        {state?.dominant && (
          <Badge variant="outline" className="font-mono text-[10px] uppercase">
            {t(`archetype.${state.dominant}`)}
          </Badge>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        {state ? (
          <>
            {rich(t(plural('doctrine.points', state.available)), {
              count: <span className="font-medium text-foreground">{state.available}</span>,
            })}
            {ticksUntilNextGrant !== null &&
              state.available < 6 &&
              t('doctrine.nextGrant', { ticks: ticksUntilNextGrant })}
          </>
        ) : (
          t('doctrine.noPlayer')
        )}
      </p>
      {state && state.unitAttack !== 4 && (
        <p className="text-[11px] text-amber-700 dark:text-amber-400/90">
          {t('doctrine.unitAttack', { bonus: state.unitAttack - 4 })}
        </p>
      )}
    </header>
  );
}

interface TabBarProps {
  readonly activeTab: 'doctrine' | 'artifacts';
  readonly onTabChange: (tab: 'doctrine' | 'artifacts') => void;
}

function TabBar({ activeTab, onTabChange }: TabBarProps) {
  const t = useT();
  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={() => onTabChange('doctrine')}
        className={`flex-1 cursor-pointer rounded-md px-2 py-1 text-[11px] font-semibold tracking-wide transition-colors ${
          activeTab === 'doctrine'
            ? 'bg-primary/10 text-primary'
            : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
        }`}
      >
        {t('doctrine.tab.doctrine')}
      </button>
      <button
        type="button"
        onClick={() => onTabChange('artifacts')}
        className={`flex-1 cursor-pointer rounded-md px-2 py-1 text-[11px] font-semibold tracking-wide transition-colors ${
          activeTab === 'artifacts'
            ? 'bg-primary/10 text-primary'
            : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
        }`}
      >
        {t('doctrine.tab.artifacts')}
      </button>
      {/* Dock affordances ride on the tab row — no extra chrome row. */}
      <PanelDockGrip id="doctrine" />
      <PanelCollapseButton id="doctrine" />
    </div>
  );
}
